import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Crown } from 'lucide-react';
import { colors, typography, spacing, radius } from '../../styles/theme';

const ProWelcomeModal = ({ isOpen, onClose }) => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Stop confetti after animation
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing[4]
        }}
      >
        {/* Confetti Effect */}
        {showConfetti && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 9999
          }}>
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  top: '-10px',
                  left: `${Math.random() * 100}%`,
                  width: '10px',
                  height: '10px',
                  background: ['#fbbf24', '#60a5fa', '#a78bfa', '#10b981', '#ec4899'][Math.floor(Math.random() * 5)],
                  opacity: 0.8,
                  animation: `fall ${2 + Math.random() * 3}s linear forwards`,
                  animationDelay: `${Math.random() * 0.5}s`
                }}
              />
            ))}
          </div>
        )}

        {/* Modal */}
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            border: `2px solid ${colors.warning}`,
            borderRadius: radius.xl,
            padding: spacing[8],
            maxWidth: '520px',
            width: '100%',
            position: 'relative',
            boxShadow: '0 25px 50px rgba(251, 191, 36, 0.3)',
            fontFamily: typography.fontSans,
            textAlign: 'center'
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: spacing[4],
              right: spacing[4],
              background: 'transparent',
              border: 'none',
              color: colors.textTertiary,
              cursor: 'pointer',
              padding: spacing[2],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: radius.sm,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(100, 116, 139, 0.2)';
              e.currentTarget.style.color = colors.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = colors.textTertiary;
            }}
          >
            <X size={20} />
          </button>

          {/* Icon */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: spacing[4]
          }}>
            <div style={{
              background: `linear-gradient(135deg, ${colors.warning} 0%, #f59e0b 100%)`,
              borderRadius: radius.full,
              padding: spacing[5],
              display: 'inline-flex',
              boxShadow: '0 8px 20px rgba(251, 191, 36, 0.4)'
            }}>
              <Crown size={40} color="white" />
            </div>
          </div>

          {/* Payment Confirmed Badge */}
          <div style={{
            display: 'inline-block',
            background: 'rgba(16, 185, 129, 0.25)',
            border: '2px solid #10b981',
            borderRadius: radius.lg,
            padding: `${spacing[2]} ${spacing[4]}`,
            marginBottom: spacing[3],
            fontSize: typography.sm,
            fontWeight: typography.bold,
            color: '#10b981',
            boxShadow: '0 0 12px rgba(16, 185, 129, 0.3)'
          }}>
            ✓ Payment Confirmed
          </div>

          {/* Title */}
          <h2 style={{
            fontSize: typography['3xl'],
            fontWeight: typography.extrabold,
            color: colors.textPrimary,
            margin: `0 0 ${spacing[3]} 0`,
            lineHeight: typography.tight
          }}>
            🎉 Congratulations!
          </h2>

          {/* Subtitle */}
          <p style={{
            fontSize: typography.xl,
            color: colors.warning,
            fontWeight: typography.bold,
            lineHeight: typography.tight,
            margin: `0 0 ${spacing[4]} 0`
          }}>
            You're now a Pro member!
          </p>

          <p style={{
            fontSize: typography.base,
            color: colors.textSecondary,
            lineHeight: typography.relaxed,
            margin: `0 0 ${spacing[2]} 0`
          }}>
            Thank you for investing in your cognitive development. 
          </p>

          <p style={{
            fontSize: typography.base,
            color: colors.textSecondary,
            lineHeight: typography.relaxed,
            margin: `0 0 ${spacing[6]} 0`
          }}>
            We're excited to support you on your journey to peak mental performance.
          </p>

          {/* Motivational message */}
          <div style={{
            background: 'rgba(251, 191, 36, 0.05)',
            border: `1px solid rgba(251, 191, 36, 0.2)`,
            borderRadius: radius.lg,
            padding: spacing[5],
            marginBottom: spacing[6],
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: typography.lg,
              fontWeight: typography.semibold,
              color: colors.textPrimary,
              marginBottom: spacing[2]
            }}>
              🚀 Your unlimited training starts now
            </div>
            <div style={{
              fontSize: typography.sm,
              color: colors.textSecondary,
              lineHeight: typography.relaxed
            }}>
              No more limits. No more restrictions. Just pure, uninterrupted cognitive training whenever you want it.
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              background: `linear-gradient(135deg, ${colors.warning} 0%, #f59e0b 100%)`,
              border: 'none',
              borderRadius: radius.lg,
              padding: `${spacing[4]} ${spacing[6]}`,
              fontSize: typography.lg,
              fontWeight: typography.bold,
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)',
              fontFamily: typography.fontSans
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(251, 191, 36, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.3)';
            }}
          >
            Start Training!
          </button>
        </div>
      </div>

      {/* Keyframe animation for confetti */}
      <style>{`
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ProWelcomeModal;

